import { WebDashboardContentSpec, WebDashboardContentSpecSlicersInner } from "@coeff/agent-types";

// ===========================================================================
// Semantic fingerprints of the dashboard spec, from the runtime's point of view.
//
// useDashboardRuntime's init effect keys on runtimeInitFingerprint instead of object
// identity, so replacing the spec (every manual edit and every save round-trips it) only
// tears the runtime down when something the init actually consumes changed. Edits outside
// the projection — filter defaults, labels, slicer order, block content — leave the
// fingerprint alone and the dashboard does not "refresh".
//
// INVARIANT: runtimeInitFingerprint must project EVERY contentSpec field the init effect
// reads (defaults excepted — those are reconciled in place, see slicerDefaultsFingerprint).
// Reading a new field in init without adding it here silently skips rebuilds that edit
// needs.
// ===========================================================================

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

// The slicer type is a discriminated union; these fields exist only on some variants.
const field = <T>(slicer: WebDashboardContentSpecSlicersInner, key: string): T | null =>
  key in slicer
    ? ((slicer as unknown as Record<string, unknown>)[key] as T | undefined) ?? null
    : null;

/**
 * What the runtime init consumes: temp-object SQL and JS table transforms (both
 * order-preserving — objects are created in array order and may depend on one another) and
 * each slicer's data-facing config, sorted by name so a pure reorder changes nothing.
 */
export const runtimeInitFingerprint = (contentSpec: WebDashboardContentSpec): string =>
  JSON.stringify({
    tempViewSql: contentSpec.create_temp_view_sql_statements ?? [],
    jsTableTransforms: contentSpec.js_table_transforms ?? [],
    slicers: (contentSpec.slicers ?? [])
      .map(slicer => ({
        name: slicer.name,
        type: slicer.type,
        options: field(slicer, "options"),
        get_options_js_function: field(slicer, "get_options_js_function"),
        get_default_value_js_function: field(slicer, "get_default_value_js_function"),
      }))
      .sort(byName),
  });

/**
 * The defaults the spec declares, which the runtime can reconcile without a rebuild:
 * literal select values and structured date selections resolve against state the last init
 * already produced.
 */
export const slicerDefaultsFingerprint = (slicers: WebDashboardContentSpecSlicersInner[]): string =>
  JSON.stringify(
    slicers
      .map(slicer => ({
        name: slicer.name,
        type: slicer.type,
        default_value: field(slicer, "default_value"),
        default_all: field(slicer, "default_all"),
        default_selection: field(slicer, "default_selection"),
      }))
      .sort(byName)
  );
